import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry on the browser side.
 * No-op when REACT_APP_SENTRY_DSN is missing (dev / preview without DSN).
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;

  // Tag prod vs preview by hostname so we can filter in the Sentry UI.
  const env =
    typeof window !== 'undefined' && window.location.hostname.endsWith('homesbyli.com')
      ? 'production'
      : 'preview';

  Sentry.init({
    dsn,
    environment: env,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,    // never replay healthy sessions (saves quota)
    replaysOnErrorSampleRate: 1.0,    // 100% replay when an error fires
    sendDefaultPii: false,
    // Drop noisy / non-actionable browser errors.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network Error',
      /Loading chunk \d+ failed/,
    ],
  });
}

function FallbackUI({ resetError }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-6">
          We&rsquo;ve been notified and are looking into it. Try again, or head back to the homepage.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={resetError}
            className="px-4 py-2 rounded-md bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium transition-colors"
            data-testid="error-boundary-retry"
          >
            Try again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors"
            data-testid="error-boundary-home"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const SentryErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary fallback={FallbackUI}>{children}</Sentry.ErrorBoundary>
);
